import { getReadableImageUrl } from "@/lib/directus";
import Image from "next/image";
import { sanitizeHTML } from "@/lib/utils";
import { toBase64 } from "@/lib/utils";
import { shimmer } from "@/lib/utils";
import ImagePreloader from "@/components/ImagePreloader";

interface HomeHeroData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  text?: {
    text?: string;
  };
}

export const fields = [
  "heading.text",
  "heading.title",
  "subheading.text", 
  "subheading.title",
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
  "text.text",
] as const;

export default function HomeHero({ data }: { data?: HomeHeroData }) {
  const imageUrl = getReadableImageUrl(
    data?.image?.image_file?.image.id || "", 
    data?.image?.image_file?.image.filename_download || ""
  ) || "";
  
  // Get image ID and filename for preloading
  const imageId = data?.image?.image_file?.image.id || "";
  const imageFilename = data?.image?.image_file?.image.filename_download || "";
  const imageAlt = data?.image?.alt || "Hero Background";

  return (
		<>
			{/* Preload the hero image for faster LCP */}
			{imageId && imageFilename && (
				<ImagePreloader
					imageId={imageId}
					filename={imageFilename}
					width={1440} // Desktop size
					quality={80}
				/>
			)}

			<section
				id='home-hero'
				className='relative w-full h-[700px] md:py-0 md:h-[791px]'
			>
				<div className='absolute inset-0 w-full h-full'>
					<div className='md:block hidden absolute inset-0 bg-black/60 md:bg-black/30 z-10' />
					<Image
						src={imageUrl}
						alt={imageAlt}
						fill
						className='object-cover md:object-[50%_-100px] object-[40%_-55px]'
						quality={90}
						priority
						sizes='100vw'
						placeholder='blur'
						blurDataURL={`data:image/svg+xml;base64,${toBase64(
							shimmer(1920, 1080)
						)}`}
						fetchPriority='high'
					/>
				</div>

				<div className='absolute inset-0 container flex items-end md:items-center z-[11] max-md:p-0'>
					<div className='text-white space-y-4 w-full md:w-[70%] lg:w-1/2 max-md:bg-[rgb(0_0_0_/_70%)] max-md:p-5 max-md:mb-[58px] md:ml-auto'>
						<h1
							className='font-freightBig md:text-[53px] text-[32px]'
							dangerouslySetInnerHTML={sanitizeHTML(
								data?.heading?.title ||
									data?.heading?.text ||
									'Dr. Julian De Silva'
							)}
						/>
						<span
							className='block md:text-2xl md:pb-10'
							dangerouslySetInnerHTML={sanitizeHTML(
								data?.subheading?.title ||
									data?.subheading?.text ||
									"London's Leading Facial Plastic Surgery Practice"
							)}
						/>
						<div
							className='space-y-4 md:text-lg [&>ul>li]:list-disc [&>ul>li]:py-1 [&>ul]:list-inside [&>ul]:pl-4'
							dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')}
						/>
					</div>
				</div>
			</section>
		</>
	)
}
